import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CheckIcon, ImageIcon, LayersIcon, LockIcon, MessageSquareIcon, PlayIcon, SendIcon, TypeIcon } from 'lucide-react';
import { CarliStars } from './CarliStars';
import { AdStudioMatrix } from './AdStudioMatrix';
import { CreativeMode, CreativeSet, FormatId, formats } from '../data/adStudio';
const modes: {
  id: CreativeMode;
  icon: typeof ImageIcon;
}[] = [{
  id: 'Static',
  icon: ImageIcon
}, {
  id: 'Carousel',
  icon: LayersIcon
}, {
  id: 'Video',
  icon: PlayIcon
}];
const panels = ['Concepts', 'Copy', 'Versions', 'Comments'] as const;
type Panel = (typeof panels)[number];
const ease = [0.23, 1, 0.32, 1] as const;
export function AdStudioEditor({
  set,
  onBack
}: {
  set: CreativeSet;
  onBack: () => void;
}) {
  const [mode, setMode] = useState<CreativeMode>(set.concepts[0]?.mode ?? 'Static');
  const [format, setFormat] = useState<FormatId>('4:5');
  const [conceptId, setConceptId] = useState(set.concepts[0]?.id ?? '');
  const [copyId, setCopyId] = useState(set.copy[0]?.id ?? '');
  const [panel, setPanel] = useState<Panel>('Concepts');
  const [instruction, setInstruction] = useState('');
  const [placements, setPlacements] = useState(set.placements);
  const concept = set.concepts.find(item => item.id === conceptId) ?? set.concepts[0];
  const copy = set.copy.find(item => item.id === copyId) ?? set.copy[0];
  const activeFormat = formats.find(item => item.id === format) ?? formats[0];
  const openComments = useMemo(() => set.comments.filter(comment => !comment.resolved).length, [set.comments]);
  const generateMissing = () => {
    setPlacements(current => current.map(placement => ({
      ...placement,
      formats: Object.fromEntries(Object.entries(placement.formats).map(([key, state]) => [key, state === 'Missing' ? 'Generating' : state])) as typeof placement.formats
    })));
  };
  return <section aria-labelledby="editor-heading" className="pb-24" data-id="element-42" data-mp="1ygafmhi92vk8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-43" data-mp="31pwpuom063sk">
        <div className="min-w-0" data-id="element-44" data-mp="2wrlf8bnvvp9a">
          <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink/55 transition-colors hover:text-[#94452E]" data-id="element-45" data-mp="2wsmsmmsdgnu6">
            <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-46" data-mp="3ko8y8853b9n3" />
            All creative sets
          </button>
          <h2 id="editor-heading" className="mt-2 font-display text-2xl font-medium tracking-tight" data-id="element-47" data-mp="3fpnx86i0klxl">{set.name}</h2>
          <p className="mt-1 text-sm text-ink/60" data-id="element-48" data-mp="3tfhpkysq9oc2">
            {set.brand} · {set.status} · updated {set.updated} by {set.owner}
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-[#BB5E40]/30 bg-[#BB5E40]/[0.06] px-3.5 py-2 text-[11px] font-semibold text-[#94452E]" data-id="element-49" data-mp="79cemeeca20p">
          <CarliStars data-id="element-50" data-mp="28eye7cw3i1u" />
          Carli is orchestrating Kive, OpenAI, Claude and Higgsfield
        </span>
      </div>

      <div className="mt-6 grid gap-5 xl:grid-cols-[minmax(0,280px)_minmax(0,1fr)_minmax(0,320px)]" data-id="element-51" data-mp="quupa20enu43">
        <BriefColumn set={set} data-id="element-52" data-mp="by3xoewz3mp1" />

        <div className="min-w-0 space-y-5" data-id="element-53" data-mp="bx2bbeaibds0">
          <div className="rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" data-id="element-54" data-mp="c08536u2z3u2">
            <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-55" data-mp="gyrp31p465js">
              <div className="flex gap-1 rounded-xl border border-ink/10 bg-cream p-1" data-id="element-56" data-mp="348w1gv28ee5">
                {modes.map(option => <button key={option.id} type="button" onClick={() => setMode(option.id)} aria-pressed={mode === option.id} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${mode === option.id ? 'bg-[#BB5E40] text-cream' : 'text-ink/60 hover:text-ink'}`} data-id="element-57" data-mp="3pcs0w6se76vh">
                    <option.icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-58" data-mp="3dcjxr6ztcdze" />
                    {option.id}
                  </button>)}
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-59" data-mp="33j301rwk2eqg">
                {formats.map(option => <button key={option.id} type="button" onClick={() => setFormat(option.id)} aria-pressed={format === option.id} title={option.usage} className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${format === option.id ? 'border-[#BB5E40] bg-[#BB5E40]/10 text-[#94452E]' : 'border-ink/12 text-ink/55 hover:border-[#BB5E40]/40'}`} data-id="element-60" data-mp="2yl1hr96yejg1">
                    {option.id}
                  </button>)}
              </div>
            </div>

            <div className="mt-6 flex justify-center rounded-2xl bg-cream/60 p-6" data-id="element-61" data-mp="ma9qsp9693so">
              <motion.figure key={`${concept?.id}-${format}-${mode}`} initial={{
              opacity: 0,
              scale: 0.97
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.24,
              ease
            }} className="w-full max-w-md overflow-hidden rounded-xl border border-ink/12 bg-paper shadow-sm" data-id="element-62" data-mp="qceg5bsl7e9i">
                <div className="relative overflow-hidden bg-ink/5" style={{
                aspectRatio: activeFormat.ratio
              }} data-id="element-63" data-mp="7g4xeb6qpzj6">
                  {concept ? <img src={concept.image} alt={concept.name} className="h-full w-full object-cover" data-id="element-64" data-mp="cgj6zxnzphjb" /> : <div className="flex h-full items-center justify-center text-xs text-ink/45" data-id="element-65" data-mp="1ntv2nl9litue">No concept selected</div>}
                  {mode === 'Carousel' && <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5" data-id="element-66" data-mp="134armzdqsjav">
                      {[0, 1, 2, 3].map(dot => <span key={dot} className={`h-1.5 rounded-full ${dot === 0 ? 'w-4 bg-cream' : 'w-1.5 bg-cream/60'}`} data-id="element-67" data-mp="y4emuxx80nu9" />)}
                    </div>}
                  {mode === 'Video' && <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-ink/70 px-2.5 py-1 text-[10px] font-semibold text-cream" data-id="element-68" data-mp="181jal26psrd6">
                      <PlayIcon className="h-3 w-3" aria-hidden="true" data-id="element-69" data-mp="fg9ibo8qoyue" />
                      0:15
                    </span>}
                </div>
                <figcaption className="space-y-2 p-4" data-id="element-70" data-mp="pcmbml0dhaom">
                  <input value={copy?.headline ?? ''} onChange={() => undefined} aria-label="Headline" className="w-full rounded-lg border border-transparent bg-transparent font-display text-lg font-medium tracking-tight text-ink outline-none transition-colors hover:border-ink/12 focus:border-[#BB5E40] focus:bg-cream/50 focus:px-2" data-id="element-71" data-mp="pb1jf21j63tx" />
                  <textarea value={copy?.body ?? ''} onChange={() => undefined} aria-label="Body copy" rows={2} className="w-full resize-none rounded-lg border border-transparent bg-transparent text-sm leading-relaxed text-ink/70 outline-none transition-colors hover:border-ink/12 focus:border-[#BB5E40] focus:bg-cream/50 focus:px-2" data-id="element-72" data-mp="1ydwd8foj10wb" />
                  <span className="inline-flex rounded-full bg-ink px-4 py-2 text-xs font-semibold text-cream" data-id="element-73" data-mp="2i3gacsv94koi">{copy?.cta}</span>
                </figcaption>
              </motion.figure>
            </div>

            <p className="mt-4 text-center text-[11px] text-ink/45" data-id="element-74" data-mp="16ob6hljdwg3b">
              {activeFormat.id} · {activeFormat.label} · {activeFormat.usage}
            </p>
          </div>

          <AdStudioMatrix placements={placements} onGenerateMissing={generateMissing} data-id="element-75" data-mp="22cy1zdcp7mwe" />
        </div>

        <div className="min-w-0 space-y-5" data-id="element-76" data-mp="1xayy82d1qnyx">
          <div className="rounded-2xl border border-ink/10 bg-paper shadow-sm" data-id="element-77" data-mp="1taeziuctrcao">
            <div className="flex gap-1 overflow-x-auto border-b border-ink/10 px-2 pt-2" data-id="element-78" data-mp="1tcjcs2wm5nnm">
              {panels.map(option => <button key={option} type="button" onClick={() => setPanel(option)} aria-selected={panel === option} role="tab" className={`whitespace-nowrap rounded-t-lg px-3 py-2 text-xs font-semibold transition-colors ${panel === option ? 'bg-[#BB5E40]/8 text-[#94452E]' : 'text-ink/55 hover:text-ink'}`} data-id="element-79" data-mp="2l24cid3c9874">
                  {option}
                  {option === 'Comments' && openComments > 0 && <span className="ml-1.5 rounded-full bg-[#BB5E40] px-1.5 py-0.5 text-[10px] text-cream" data-id="element-80" data-mp="37a9tk29zohw9">{openComments}</span>}
                </button>)}
            </div>

            <div className="max-h-[520px] overflow-y-auto p-4" data-id="element-81" data-mp="3h8pko590nr5r">
              {panel === 'Concepts' && <ul className="space-y-2.5" data-id="element-82" data-mp="3l9sxeivrp72r">
                  {set.concepts.map(item => <li key={item.id} data-id="element-83" data-mp="3q9fb0tficpc3">
                      <button type="button" onClick={() => {
                  setConceptId(item.id);
                  setMode(item.mode);
                }} aria-pressed={conceptId === item.id} className={`flex w-full gap-3 rounded-xl border p-2.5 text-left transition-colors ${conceptId === item.id ? 'border-[#BB5E40] bg-[#BB5E40]/[0.06]' : 'border-ink/12 hover:border-[#BB5E40]/35'}`} data-id="element-84" data-mp="3q8co0wlb371g">
                        <img src={item.image} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" data-id="element-85" data-mp="10gdasw03ulvp" />
                        <span className="min-w-0" data-id="element-86" data-mp="15f6q8tyycmyi">
                          <span className="block text-xs font-semibold text-ink" data-id="element-87" data-mp="1adhzp3lc4vg7">{item.name}</span>
                          <span className="mt-0.5 block text-[11px] leading-relaxed text-ink/55" data-id="element-88" data-mp="1f483ice44q8s">{item.note}</span>
                          <span className="mt-1 inline-block rounded-full bg-ink/7 px-2 py-0.5 text-[10px] font-semibold text-ink/55" data-id="element-89" data-mp="1j6bg54ciq0vn">{item.mode}</span>
                        </span>
                      </button>
                    </li>)}
                </ul>}

              {panel === 'Copy' && <ul className="space-y-2.5" data-id="element-90" data-mp="1y1rh7tbimx1j">
                  {set.copy.map(item => <li key={item.id} data-id="element-91" data-mp="3uwe0lfiwth4s">
                      <button type="button" onClick={() => setCopyId(item.id)} aria-pressed={copyId === item.id} className={`w-full rounded-xl border p-3 text-left transition-colors ${copyId === item.id ? 'border-[#BB5E40] bg-[#BB5E40]/[0.06]' : 'border-ink/12 hover:border-[#BB5E40]/35'}`} data-id="element-92" data-mp="3uxfctnbnw9o3">
                        <span className="flex items-start gap-2" data-id="element-93" data-mp="2397lxxdagl7d">
                          <TypeIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-94" data-mp="2zyp2x8hq1tod" />
                          <span className="min-w-0" data-id="element-95" data-mp="2vwbycnepo1t6">
                            <span className="block text-xs font-semibold text-ink" data-id="element-96" data-mp="2vur28tcr0at7">{item.headline}</span>
                            <span className="mt-1 block text-[11px] leading-relaxed text-ink/60" data-id="element-97" data-mp="2qzbriaa1ev10">{item.body}</span>
                            <span className="mt-1.5 block text-[10px] font-medium text-ink/45" data-id="element-98" data-mp="1fi297r2na91a">{item.note}</span>
                          </span>
                        </span>
                      </button>
                    </li>)}
                </ul>}

              {panel === 'Versions' && <ol className="space-y-3" data-id="element-99" data-mp="1gyg18jmvbg5a">
                  {set.versions.map(version => <li key={version.id} className="flex gap-3" data-id="element-100" data-mp="130j5fpg8ndro">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cream text-[10px] font-bold text-ink/60" data-id="element-101" data-mp="17ysxvsakw09l">
                        {version.label}
                      </span>
                      <span className="min-w-0" data-id="element-102" data-mp="y0wph890zjf6">
                        <span className="block text-xs font-semibold text-ink" data-id="element-103" data-mp="y2pxorzaebpa">{version.summary}</span>
                        <span className="mt-0.5 block text-[11px] text-ink/50" data-id="element-104" data-mp="k75gc0z7i1jz">{version.author} · {version.when}</span>
                      </span>
                    </li>)}
                </ol>}

              {panel === 'Comments' && <div className="space-y-3" data-id="element-105" data-mp="f8u6vrctpt2a">
                  {set.comments.length === 0 && <p className="py-6 text-center text-xs text-ink/50" data-id="element-106" data-mp="3rsy7eq1mp8yu">No comments yet.</p>}
                  {set.comments.map(comment => <div key={comment.id} className={`rounded-xl border p-3 ${comment.resolved ? 'border-ink/10 bg-cream/40' : 'border-[#BB5E40]/25 bg-[#BB5E40]/[0.04]'}`} data-id="element-107" data-mp="3mwhi3sip0ok6">
                      <div className="flex items-baseline justify-between gap-2" data-id="element-108" data-mp="emy5ek830t3r">
                        <span className="text-xs font-semibold text-ink" data-id="element-109" data-mp="9o4pkl5boq0f">{comment.author}</span>
                        <span className="text-[10px] text-ink/45" data-id="element-110" data-mp="9mac1rwds06d">{comment.when}</span>
                      </div>
                      <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-ink/40" data-id="element-111" data-mp="je7sbd28xiby">{comment.role}</p>
                      <p className="mt-1.5 text-[11px] leading-relaxed text-ink/70" data-id="element-112" data-mp="xcxrqzcjfu3h">{comment.body}</p>
                      {comment.resolved && <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-positive" data-id="element-113" data-mp="ta13ot48b5s5">
                          <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-114" data-mp="2rtxo47ezigf3" />
                          Resolved
                        </span>}
                    </div>)}
                  <div className="flex gap-2 pt-1" data-id="element-115" data-mp="31k8v62tyt5q7">
                    <MessageSquareIcon className="mt-2 h-3.5 w-3.5 shrink-0 text-ink/40" aria-hidden="true" data-id="element-116" data-mp="31ixq7zos9rxi" />
                    <input placeholder="Leave a comment" aria-label="Leave a comment" className="w-full rounded-lg border border-ink/12 bg-cream/50 px-3 py-2 text-xs outline-none focus:border-[#BB5E40]" data-id="element-117" data-mp="23y376ylskxd3" />
                  </div>
                </div>}
            </div>
          </div>

          <div className="rounded-2xl border border-[#BB5E40]/30 bg-[#BB5E40]/[0.05] p-4" data-id="element-118" data-mp="3p8s6ysc840n1">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-119" data-mp="3t9bz1hhxwe2b">
              <CarliStars data-id="element-120" data-mp="1hpnx1cysv0e1" />
              Ask Carli
            </p>
            <p className="mt-1.5 text-[11px] leading-relaxed text-ink/60" data-id="element-121" data-mp="1dn9gizilscpx">
              Describe the change in plain language. Carli picks the right model and re-renders the affected formats.
            </p>
            <div className="mt-3 flex gap-2" data-id="element-122" data-mp="13vn7n9tqmpqc">
              <label htmlFor="carli-instruction" className="sr-only" data-id="element-123" data-mp="ywttdf6mmup4">Instruction for Carli</label>
              <input id="carli-instruction" value={instruction} onChange={event => setInstruction(event.target.value)} placeholder="Warmer light, more headroom on vertical" className="w-full rounded-full border border-[#BB5E40]/30 bg-paper px-4 py-2.5 text-xs outline-none focus:border-[#BB5E40] focus:ring-2 focus:ring-[#BB5E40]/20" data-id="element-124" data-mp="txz3xue5m7w1" />
              <button type="button" disabled={!instruction.trim()} className="inline-flex shrink-0 items-center justify-center rounded-full bg-[#BB5E40] px-3.5 text-cream transition-colors hover:bg-[#A34E33] disabled:cursor-not-allowed disabled:bg-ink/15 disabled:text-ink/40" aria-label="Send instruction to Carli" data-id="element-125" data-mp="2fcd6u8zquhag">
                <SendIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-126" data-mp="2ufenp40aum3r" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-ink/10 bg-paper/95 backdrop-blur" data-id="element-127" data-mp="3i2393zoiowp4">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-center justify-between gap-3 px-5 py-3 sm:px-8" data-id="element-128" data-mp="3m36o6jyqqopa">
          <p className="text-xs text-ink/60" data-id="element-129" data-mp="3d4kzs86l8fgl">
            <span className="font-semibold text-ink" data-id="element-130" data-mp="3d39ta0lrctls">{set.name}</span> · {set.assets} assets · {set.status}
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-131" data-mp="1wso2cgq30bey">
            <button type="button" className="rounded-full border border-ink/15 px-4 py-2 text-xs font-semibold text-ink/70 transition-colors hover:bg-cream" data-id="element-132" data-mp="21pxxmd05jhap">
              Save draft
            </button>
            <button type="button" className="rounded-full border border-ink/15 px-4 py-2 text-xs font-semibold text-ink/70 transition-colors hover:bg-cream" data-id="element-133" data-mp="1p96dqoxzcvbj">
              Request changes
            </button>
            <button type="button" className="inline-flex items-center gap-1.5 rounded-full bg-[#BB5E40] px-4 py-2 text-xs font-semibold text-cream transition-colors hover:bg-[#A34E33]" data-id="element-134" data-mp="1aheahbbgn7q6">
              <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-135" data-mp="1k9jzqdabvb1o" />
              Approve set
            </button>
          </div>
        </div>
      </div>
    </section>;
}
function BriefColumn({
  set
}: {
  set: CreativeSet;
}) {
  const rows: {
    label: string;
    value: string;
  }[] = [{
    label: 'Inventory',
    value: set.brief.inventory
  }, {
    label: 'Objective',
    value: set.brief.objective
  }, {
    label: 'Audience',
    value: set.brief.audience
  }, {
    label: 'Offer',
    value: set.brief.offer
  }, {
    label: 'Landing page',
    value: set.brief.landingPage
  }, {
    label: 'Angle',
    value: set.brief.angle
  }, {
    label: 'Channel',
    value: set.brief.channel
  }, {
    label: 'Asset type',
    value: set.brief.assetType
  }];
  return <aside className="min-w-0 rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm" data-id="element-136" data-mp="g46gqo5pclwj">
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-137" data-mp="l3jew520xfef">
        <CarliStars data-id="element-138" data-mp="p4uwbnaquubj" />
        Creative brief
      </p>
      <dl className="mt-4 space-y-3.5" data-id="element-139" data-mp="tz8vm0sj1llh">
        {rows.map(row => <div key={row.label} data-id="element-140" data-mp="yxk2q3rfti00">
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-141" data-mp="yvplp0jaea17">{row.label}</dt>
            <dd className="mt-0.5 text-xs leading-relaxed text-ink/75" data-id="element-142" data-mp="13xooa877d2x3">{row.value}</dd>
          </div>)}
      </dl>

      <div className="mt-5 border-t border-ink/10 pt-4" data-id="element-143" data-mp="18su7gz9803hh">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-144" data-mp="2te0nsdyqoe6k">Brand restrictions</p>
        <ul className="mt-2 space-y-1.5" data-id="element-145" data-mp="2odcv7nuv3pa8">
          {set.brief.restrictions.map(restriction => <li key={restriction} className="flex gap-2 text-[11px] leading-relaxed text-ink/70" data-id="element-146" data-mp="2jfl774xc8nbb">
              <LockIcon className="mt-0.5 h-3 w-3 shrink-0 text-[#BB5E40]" aria-hidden="true" data-id="element-147" data-mp="2ehjcfmqjuzk0" />
              {restriction}
            </li>)}
        </ul>
      </div>
    </aside>;
}